import { from, of, delay, switchMap, retry, EMPTY, throwError, finalize, Subject, tap, startWith, share, ReplaySubject, merge, interval, Observable, catchError, debounceTime, distinctUntilChanged } from 'rxjs';

import { sessionToken, forgetSession, gotoLogin } from './session.js';

let reloadJobsTrigger$ = new Subject();

export let jobsQueueLength$ = merge(
    of(undefined),
    interval(10*1000),
    reloadJobsTrigger$,
).pipe(
    debounceTime(100),
    switchMap(() => fetchJson('/jobs/queue-length').pipe(
        catchError(err => {
            console.warn('Unable to update queue-length:', err);
            return EMPTY;
        }),
    )),
    distinctUntilChanged(),
    share({
        connector: () => new ReplaySubject(1),
        resetOnError: true,
        resetOnRefCountZero: true,
        resetOnComplete: true,
    }),
);

export let myJobs$ = merge(
    of(undefined),
    interval(10*1000),
    reloadJobsTrigger$,
).pipe(
    debounceTime(100),
    switchMap(() => fetchJson('/jobs/mine').pipe(
        catchError(err => {
            console.warn('Unable to update my jobs:', err);
            return EMPTY;
        }),
    )),
    share({
        connector: () => new ReplaySubject(1),
        resetOnError: true,
        resetOnRefCountZero: true,
        resetOnComplete: true,
    }),
);

export function submitJob(job) {
    return fetchJson('/jobs', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(job),
    }).pipe(
        tap(() => reloadJobsTrigger$.next()),
    );
}

export function updateJobFlag(jobId, flagName, flagValue) {
    let body = {};
    body[flagName] = flagValue;
    return fetchJson(`/job/${jobId}/flags`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    }).pipe(
        tap(() => reloadJobsTrigger$.next()),
    );
}

export function archiveJob(jobId) {
    return fetchJson(`/job/${jobId}`, {
        method: 'DELETE',
    }).pipe(
        tap(() => reloadJobsTrigger$.next()),
    );
}

export function getJob(jobId) {
    return fetchJson(`/job/${jobId}`, {
        method: 'GET',
    });
}

export function craftPrompts(req) {
    return fetchJson('/craft-prompts', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(req),
    });
}

export function fetchImg(url) {
    return fetchBlobUrl(url, {
        headers: {
            'Accept': 'image/png',
        },
    });
}

function fetchBlobUrl(url, opts={}) {
    return genericFetch(url, {
        ...opts,
        load: resp => from(resp.blob()),
    }).pipe(
        switchMap(blob => new Observable(observer => {
            let url = URL.createObjectURL(blob);
            observer.next(url);
            return () => {
                URL.revokeObjectURL(url);
            };
        })),
    );
}

function fetchJson(url, opts={}) {
    return genericFetch(url, {
        ...opts,
        load: resp => from(resp.json()),
    });
}

function genericFetch(url, opts) {
    return of(undefined).pipe(
        switchMap(() => {
            let ctrl = new AbortController();
            let headers = {
                ...(opts.headers || {}),
                'Authorization': `Bearer ${sessionToken}`,
            };
            let obs$ = from(fetch(`/api${url}`, {
                ...opts,
                headers,
                signal: ctrl.signal,
            }));
            if ((opts.method || 'GET') === 'GET') {
                obs$ = obs$.pipe(
                    retry({
                        count: 3,
                        delay: 2000 + 1000*Math.random(),
                    }),
                );
            }
            return obs$.pipe(
                switchMap(resp => {
                    if (resp.status === 403) {
                        forgetSession();
                        gotoLogin();
                        return EMPTY;
                    }
                    if (!resp.ok) {
                        return throwError(resp);
                    }
                    return opts.load(resp);
                }),
                finalize(() => ctrl.abort()),
            );
        }),
    );
}
